import { useEffect, useMemo, useState, useRef } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import API_ROUTES from "../../api-routes";
import apiService from "../../services/service";
import { z } from "zod";
import { useSelector } from "react-redux";
import useFileUpload from "~/utils/useFileUpload";
import { getPeriodFromFileYearMonth } from "~/utils/fileUtils";

const useOfficersForm = ({
  type,
  setShowDialog,
  regData,
  setRegData,
  resetRegData,
  isNewOfficer,
}) => {
  const [showOfficersRegistration, setShowOfficersRegistration] =
    useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [btnToggle, setBtnToggle] = useState(true);
  const [formDetails, setFormDetails] = useState({});
  const [isCongrats, setIsCongrats] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [entity, setEntity] = useState({});
  const [entityLoading, setEntityLoading] = useState(true);
  const [files, setFiles] = useState([]);

  const { id } = useParams();
  const location = useLocation();
  const { id: userId } = useSelector((state) => state.login);
  const { uploadFile, updateTag, uuidToUlid, getFileVersion } = useFileUpload();
  const navigate = useNavigate();
  const { current: roles } = useRef([
    {
      name: "President and Chief Executive Officer",
      code: "President and Chief Executive Officer",
    },
    { name: "Vice President", code: "Vice President" },
    { name: "Secretary", code: "Secretary" },
    { name: "Chief Financial Officer", code: "Chief Financial Officer" },
    { name: "Board Member", code: "Board Member" },
  ]);

  const { current: actingAsOptions } = useRef([
    {
      name: "General",
      code: "General",
    },
    { name: "Regulatory", code: "Regulatory" },
    { name: "HR", code: "HR" },
    { name: "Finance", code: "Finance" },
    { name: "Legal", code: "Legal" },
    { name: "Limited", code: "Limited" },
  ]);

  useEffect(() => {
    setActiveIndex(0);
  }, []);

  const handleBack = () => {
    setActiveIndex((prevActiveStep) => Math.max(prevActiveStep - 1, 0));
  };

  const handleOfficerClick = () => {
    setShowOfficersRegistration(true);
  };

  const handleOfficerSubmit = async () => {
    setIsLoading(true);
    let tags, metadata;
    const attachments = [];
    if (regData) {
      const officerData = {
        name: regData.name || "",
        other_name: regData.other_name || "",
        last_name: regData.last_name || "",
        preferred_name: regData.preferred_name || "",
        role: regData.role.name || "",
        title: regData.title || "",
        email: regData.email || "",
        country: regData.country.code || "",
        phone_code: regData.phone_code || "",
        phone: regData.phone || "",
        type,
        entity: id || "",
        expiration_date: regData.expiration_date || null,
        acting_as: regData.acting_as.name || null,
      };

      const apiRoute = API_ROUTES.ADD_OFFICER;
      try {
        const formData = new FormData();
        const fileOutput = [];
        for (let i = 0; i < files.length; i++) {
          const file = files[i]?.file;
          formData.append("file", file);
          let fileData = {
            fileName: file?.fileName,
            category: file?.category,
            subCategory: file?.subcategory,
          };
          if (file?.description) {
            fileData.description = file?.description;
          }
          fileOutput.push(fileData);
        }

        formData.append("officer", JSON.stringify(officerData));
        formData.append("filesData", JSON.stringify(fileOutput));

        tags = {
          entity_id: uuidToUlid(id),
          country: entity?.country,
          temporary_file: "true",
          year: new Date().getFullYear().toString(),
        };
        for (let i = 0; i < files.length; i++) {
          const file = files[i]?.file;
          const uploadData = await uploadFile(file, file.fileName, {
            entityId: id,
            serviceLineCode: "ENTITIES_PLUS",
            fileName: file.name,
          });
          attachments.push({
            url: uploadData.url,
            fileName: uploadData.id,
            name: file.fileName,
          });
        }

        const res = await apiService(apiRoute, "POST", {
          body: { ...officerData, attachments },
          replace: { id },
          schema: z.object({
            id: z.string(),
          }),
        });
        tags = { ...tags, officer: uuidToUlid(res.data.id) };
        for (let i = 0; i < files.length; i++) {
          const file = files[i]?.file;
          const blobName = attachments[i]?.fileName;
          tags = {
            ...tags,
            category: file?.category,
            subCategory: file?.subcategory,
            temporary_file: "false",
            ...(getPeriodFromFileYearMonth(file?.fileYearMonth) && {
              period: getPeriodFromFileYearMonth(file.fileYearMonth),
            }),
          };
          const url = attachments[i]?.url;
          const version =
            file?.versionNumber.toString() || `${await getFileVersion(tags)}`;
          metadata = {
            original_file_name: encodeURIComponent(file.name),
            file_size: file.size.toString(),
            file_type: file.type,
            user: userId,
            file_name: file.fileName,
            version,
            created_at: new Date().toISOString(),
            file_object_type: "OFFICER",
            url,
            ...(file?.description && {
              description: encodeURIComponent(file?.description),
            }),
          };
          await updateTag(blobName, tags, metadata);
        }
        if (res.status) {
          resetRegData();
          setShowDialog(false);
          setFiles([]);
          setIsCongrats(true);
        } else {
          console.log("Not getting response");
        }
        setIsLoading(false);
      } catch (error) {
        console.error(error);
        setIsLoading(false);
      }
    }
  };

  // const entityId = regData?.entityId;
  const fetchEntity = async () => {
    setEntityLoading(true);
    const { data } = await apiService(API_ROUTES.FETCH_ENTITY, "GET", {
      replace: { id },
    });
    setEntity(data);
    setEntityLoading(false);
  };

  useEffect(() => {
    if (isNewOfficer) fetchEntity();
  }, []);

  useEffect(() => {
    if (typeof resetRegData === "function") {
      resetRegData();
    }
  }, [navigate]);

  const checkValue = (data) =>
    Object.values(data).some((val) =>
      val && typeof val === "object"
        ? checkValue(val)
        : val !== "" && val !== false && val !== null,
    );

  const handleDialogToggle = () => {
    if (regData) {
      const hasValue = checkValue(regData);
      setShowDialog(hasValue);
    }
  };

  useEffect(() => {
    handleDialogToggle();
  }, [regData]);

  const officerConfig = useMemo(() => {
    if (type === "officer") {
      return {
        breadcrumbType: "Appointments & Ownership",
        pageTitle: "Add New Officer",
        sidebarDescription:
          "Complete the necessary steps to add a new officer to this entity.",
        formHeader: "Officer Details",
        formDescription: "Enter all the details for this officer.",
      };
    } else if (type === "power-of-attorney") {
      return {
        breadcrumbType: "Appointments & Ownership",
        pageTitle: "Add New Power of Attorney",
        sidebarDescription:
          "Complete the necessary steps to add a new Power of Attorney to this entity.",
        formHeader: "Power of Attorney Details",
        formDescription: "Enter all the details for this Attorney.",
      };
    }
  }, [type]);

  return {
    handleOfficerClick,
    showOfficersRegistration,
    handleBack,
    activeIndex,
    setActiveIndex,
    roles,
    actingAsOptions,
    entityId: id,
    btnToggle,
    setBtnToggle,
    formDetails,
    setFormDetails,
    isCongrats,
    setIsCongrats,
    isLoading,
    setIsLoading,
    entity,
    setEntity,
    entityLoading,
    handleOfficerSubmit,
    files,
    setFiles,
    officerConfig,
  };
};

export default useOfficersForm;
